import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type { GeometryPadWidgetConfig } from '../geometrypad.model';

/**
 * Test preset: Canvas selection radio group on a triangle.
 *
 * Three radio buttons placed near each edge of the triangle.
 * Clicking one highlights that edge in sky color and greys out the others.
 */
export const TEST_CANVAS_SELECTION_TRIANGLE_PRESET: GeometryPadWidgetConfig = {
  id: 'test-canvas-selection-triangle',
  type: 'geometrypad',
  params: {
    elements: {
      t1: {
        type: 'Triangle',
        params: { a: 5, b: 4, c: 6 },
      },
    },
  },
  display: {
    geometry: {
      shape: { coloring: true },
      label: { names: 'tiered', values: 'tiered', coloring: true },
    },
  },
  behavior: {
    controls: [
      {
        id: 'cs-edge-a',
        type: WIDGET_CONTROL_TYPE.CanvasSelection,
        label: 'Side a',
        location: { x: 't1.edge.BC.midpoint.x', y: 't1.edge.BC.midpoint.y' },
        group: 'edge-select',
        selected: true,
        trigger: {
          action: 'PATCH_DISPLAY',
          payload: {
            't1.edge.BC': { line: { color: 'sky', strokeWidth: 3, label: { color: 'sky' } } },
            't1.edge.CA': { line: { color: 'neutral', strokeWidth: 1, label: { color: 'neutral' } } },
            't1.edge.AB': { line: { color: 'neutral', strokeWidth: 1, label: { color: 'neutral' } } },
          },
        },
      },
      {
        id: 'cs-edge-b',
        type: WIDGET_CONTROL_TYPE.CanvasSelection,
        label: 'Side b',
        location: { x: 't1.edge.CA.midpoint.x', y: 't1.edge.CA.midpoint.y' },
        group: 'edge-select',
        trigger: {
          action: 'PATCH_DISPLAY',
          payload: {
            't1.edge.CA': { line: { color: 'sky', strokeWidth: 3, label: { color: 'sky' } } },
            't1.edge.BC': { line: { color: 'neutral', strokeWidth: 1, label: { color: 'neutral' } } },
            't1.edge.AB': { line: { color: 'neutral', strokeWidth: 1, label: { color: 'neutral' } } },
          },
        },
      },
      {
        id: 'cs-edge-c',
        type: WIDGET_CONTROL_TYPE.CanvasSelection,
        label: 'Side c',
        location: { x: 't1.edge.AB.midpoint.x', y: 't1.edge.AB.midpoint.y' },
        group: 'edge-select',
        trigger: {
          action: 'PATCH_DISPLAY',
          payload: {
            't1.edge.AB': { line: { color: 'sky', strokeWidth: 3, label: { color: 'sky' } } },
            't1.edge.BC': { line: { color: 'neutral', strokeWidth: 1, label: { color: 'neutral' } } },
            't1.edge.CA': { line: { color: 'neutral', strokeWidth: 1, label: { color: 'neutral' } } },
          },
        },
      },
    ],
  },
};

/**
 * Test preset: Mixed canvas selection — radio group + standalone toggle.
 *
 * A rectangle with:
 * - Radio group (2 buttons): select width vs height for highlighting
 * - Standalone toggle: toggle diagonal visibility
 */
export const TEST_CANVAS_SELECTION_MIXED_PRESET: GeometryPadWidgetConfig = {
  id: 'test-canvas-selection-mixed',
  type: 'geometrypad',
  params: {
    elements: {
      r1: {
        type: 'Rectangle',
        params: { w: 6, h: 4 },
      },
      d1: {
        type: 'Diagonal',
        params: { of: 'r1' },
      },
    },
  },
  display: {
    geometry: {
      shape: { coloring: true },
      label: { names: 'tiered', values: 'tiered', coloring: true },
    },
  },
  behavior: {
    controls: [
      // Radio group: select width or height
      {
        id: 'cs-width',
        type: WIDGET_CONTROL_TYPE.CanvasSelection,
        label: 'Width',
        location: { x: 'r1.edge.bottom.midpoint.x', y: 'r1.edge.bottom.midpoint.y' },
        group: 'dimension',
        selected: true,
        trigger: {
          action: 'PATCH_DISPLAY',
          payload: {
            'r1.edge.bottom': { line: { color: 'sky', strokeWidth: 3 } },
            'r1.edge.right': { line: { color: 'neutral', strokeWidth: 1 } },
          },
        },
      },
      {
        id: 'cs-height',
        type: WIDGET_CONTROL_TYPE.CanvasSelection,
        label: 'Height',
        location: { x: 'r1.edge.right.midpoint.x', y: 'r1.edge.right.midpoint.y' },
        group: 'dimension',
        trigger: {
          action: 'PATCH_DISPLAY',
          payload: {
            'r1.edge.right': { line: { color: 'pink', strokeWidth: 3 } },
            'r1.edge.bottom': { line: { color: 'neutral', strokeWidth: 1 } },
          },
        },
      },
      // Standalone toggle: diagonal highlight
      {
        id: 'cs-diagonal',
        type: WIDGET_CONTROL_TYPE.CanvasSelection,
        label: 'Highlight diagonal',
        location: { x: 'r1.angleCenter.A.x', y: 'r1.angleCenter.A.y' },
        trigger: {
          action: 'PATCH_DISPLAY',
          payload: {
            d1: { line: { color: 'green', strokeWidth: 2 } },
          },
        },
        triggerOff: {
          action: 'PATCH_DISPLAY',
          payload: {
            d1: { line: { color: 'neutral', strokeWidth: 1 } },
          },
        },
      },
    ],
  },
};
